import { supabase } from '../lib/supabase';

export const adminService = {
  // Check if current user is admin
  async isCurrentUserAdmin() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return false;

      const { data, error } = await supabase?.from('user_profiles')?.select('role')?.eq('id', user?.id)?.single();
      
      if (error) return false;
      return data?.role === 'admin';
    } catch (error) {
      return false;
    }
  },

  // Get all users with their profiles (admin only)
  async getAllUsers() {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select(`
          id,
          full_name,
          email,
          role,
          membership_tier,
          is_active,
          created_at,
          avatar_url,
          collectible_types,
          subscribe_newsletter
        `)?.order('created_at', { ascending: false });

      return { data, error };
    } catch (error) {
      return { data: null, error: { message: 'Network error loading users' } };
    }
  },

  // Get user by ID
  async getUserById(id) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select(`
          id,
          full_name,
          email,
          role,
          membership_tier,
          is_active,
          created_at,
          avatar_url,
          collectible_types,
          subscribe_newsletter,
          settings
        `)?.eq('id', id)?.single();

      return { data, error };
    } catch (error) {
      return { data: null, error: { message: 'Network error loading user' } };
    }
  },

  // Create new user profile (admin only)
  async createUser(userData) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.insert([{
          ...userData,
          created_at: new Date()?.toISOString()
        }])?.select()?.single();

      return { data, error };
    } catch (error) {
      return { data: null, error: { message: 'Network error creating user' } };
    }
  },

  // Update user profile (admin only)
  async updateUser(id, userData) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update({
          ...userData,
          updated_at: new Date()?.toISOString()
        })?.eq('id', id)?.select()?.single();

      return { data, error };
    } catch (error) {
      return { data: null, error: { message: 'Network error updating user' } };
    }
  },

  // Delete user (admin only)
  async deleteUser(id) {
    try {
      // Soft delete by marking as inactive
      const { data, error } = await supabase?.from('user_profiles')?.update({
          is_active: false,
          updated_at: new Date()?.toISOString()
        })?.eq('id', id)?.select()?.single();
      
      return { data, error };
    } catch (error) {
      return { data: null, error: { message: 'Network error deleting user' } };
    }
  },

  // Update user role (admin only)
  async updateUserRole(id, role) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update({
          role,
          updated_at: new Date()?.toISOString()
        })?.eq('id', id)?.select()?.single();

      return { data, error };
    } catch (error) {
      return { data: null, error: { message: 'Network error updating role' } };
    }
  },

  // Toggle user active status (admin only)
  async toggleUserStatus(id, isActive) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update({
          is_active: isActive,
          updated_at: new Date()?.toISOString()
        })?.eq('id', id)?.select()?.single();

      return { data, error };
    } catch (error) {
      return { data: null, error: { message: 'Network error updating status' } };
    }
  },

  // Update user membership tier (admin only)
  async updateMembershipTier(id, tier) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update({
          membership_tier: tier,
          updated_at: new Date()?.toISOString()
        })?.eq('id', id)?.select()?.single();

      return { data, error };
    } catch (error) {
      return { data: null, error: { message: 'Network error updating membership' } };
    }
  },

  // Get system statistics (admin only)
  async getSystemStats() {
    try {
      const [usersResult, collectiblesResult, collectionsResult] = await Promise.all([
        supabase?.from('user_profiles')?.select('id, is_active, created_at')?.order('created_at'),
        supabase?.from('collectibles')?.select('id, created_at')?.order('created_at'),
        supabase?.from('collections')?.select('id, total_value')
      ]);

      const users = usersResult?.data || [];
      const collectibles = collectiblesResult?.data || [];
      const collections = collectionsResult?.data || [];

      // Calculate stats
      const totalUsers = users?.length;
      const activeUsers = users?.filter(u => u?.is_active)?.length;
      const totalCollectibles = collectibles?.length;
      const totalValue = collections?.reduce((sum, c) => sum + (parseFloat(c?.total_value) || 0), 0);

      // Growth calculations (mock data for demonstration)
      const userGrowth = 12.5; // % growth from previous period
      const collectibleGrowth = 8.3;

      return {
        data: {
          totalUsers,
          activeUsers,
          totalCollectibles,
          totalValue,
          userGrowth,
          collectibleGrowth,
          totalCollections: collections?.length
        },
        error: null
      };
    } catch (error) {
      return { data: null, error };
    }
  },

  // Get user activity summary (admin only)
  async getUserActivitySummary() {
    try {
      const { data, error } = await supabase?.from('user_activities')?.select(`
          activity_type,
          created_at,
          user_profiles!inner(full_name, email)
        `)?.order('created_at', { ascending: false })?.limit(50);

      return { data, error };
    } catch (error) {
      return { data: null, error: { message: 'Network error loading activity' } };
    }
  },

  // Bulk user operations (admin only)
  async bulkUpdateUsers(userIds, updates) {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update({
          ...updates,
          updated_at: new Date()?.toISOString()
        })?.in('id', userIds)?.select();

      return { data, error };
    } catch (error) {
      return { data: null, error: { message: 'Network error updating users' } };
    }
  }
};

export default adminService;